import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowLeft } from "lucide-react";
import { Logo, Panel, Btn, Field, Input } from "@/components/kit";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { PLANS, planMeta } from "@/lib/plans";

export default function BusinessRegister() {
  const [params] = useSearchParams();
  const initialPlan = ["launch", "elevate", "signature"].includes(params.get("plan")) ? params.get("plan") : "elevate";
  const [form, setForm] = useState({ business_name: "", owner_name: "", email: "", password: "", accept_terms: false, plan: initialPlan });
  const [busy, setBusy] = useState(false);
  const { registerBusiness } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const chosen = planMeta(form.plan);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) return toast.error("Please accept the Terms & Conditions");
    setBusy(true);
    try {
      const user = await registerBusiness(form);
      toast.success("Business created! Check your email to verify your account.");
      navigate(homeForRole(user.role));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="bd-page flex flex-col">
      <header className="sticky top-0 z-30 border-b border-[#2E1256] bg-[#0B031E]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" data-testid="header-logo"><Logo /></Link>
          <button data-testid="back-home" onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-200 hover:text-white transition-colors">
            <ArrowLeft size={16} /> Back to home
          </button>
        </div>
      </header>
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <Panel className="w-full max-w-md p-8 bd-fade-up">
        <h1 className="font-head text-2xl font-bold text-white mb-1">Register your business</h1>
        <p className="text-sm text-purple-300/70 mb-5">Start a 30-day free trial — no card required. You'll verify your email next.</p>

        <div className="mb-5">
          <span className="text-xs font-semibold tracking-wider uppercase text-purple-300/70">Your plan</span>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {PLANS.map((p) => (
              <button type="button" key={p.id} data-testid={`reg-plan-${p.id}`} onClick={() => setForm({ ...form, plan: p.id })}
                className={`rounded-xl border px-2 py-2.5 text-center transition-colors ${form.plan === p.id ? "border-[#FF2A6D] bg-[#FF2A6D]/10" : "border-[#2E1256] bg-[#120429] hover:border-[#451B7F]"}`}>
                <div className="text-sm font-bold text-white">{p.name}</div>
                <div className="text-[11px] text-purple-300/70">{p.price}</div>
              </button>
            ))}
          </div>
          <div className="mt-2 text-xs text-purple-400/70 flex items-center gap-1.5"><Check size={13} className="text-emerald-400" /> {chosen.tagline}</div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Business name"><Input data-testid="biz-name" required value={form.business_name} onChange={set("business_name")} placeholder="Grand Beet Resort & Spa" /></Field>
          <Field label="Your name (Owner / GM)"><Input data-testid="biz-owner" required value={form.owner_name} onChange={set("owner_name")} placeholder="Jordan Wade" /></Field>
          <Field label="Work email"><Input data-testid="biz-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@business.com" /></Field>
          <Field label="Password"><Input data-testid="biz-password" type="password" required value={form.password} onChange={set("password")} placeholder="At least 6 characters" /></Field>
          <label className="flex items-start gap-2 text-sm text-purple-200 cursor-pointer">
            <input data-testid="biz-terms" type="checkbox" className="mt-1" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} />
            <span>I accept the BeetDrop <span className="text-[#FF6B7D] font-semibold">Terms & Conditions</span> and Privacy Policy.</span>
          </label>
          <Btn type="submit" className="w-full" disabled={busy} data-testid="biz-submit">{busy ? "Creating…" : "Start free trial"}</Btn>
        </form>
        <div className="mt-5 text-center text-sm text-purple-300/70">
          Already registered? <Link to="/login" className="font-semibold text-[#FF6B7D]">Sign in</Link>
        </div>
      </Panel>
      </div>
    </div>
  );
}
